"use client"

import * as React from "react"
import { motion, useScroll, useSpring, useReducedMotion } from "motion/react"
import type { HTMLMotionProps } from "motion/react"
import { cn } from "@/lib/utils"

export interface ScrollProgressProps extends Omit<HTMLMotionProps<"div">, "style"> {
    /** Use brand gradient (pink → purple). Default: true */
    gradient?: boolean
    /** Fallback solid color when gradient is false */
    color?: string
    /** Hide when content doesn't require scrolling. Default: true */
    hideWhenNotScrollable?: boolean
    /** Spring animation configuration */
    springConfig?: {
        stiffness?: number
        damping?: number
    }
}

const ScrollProgress = React.forwardRef<HTMLDivElement, ScrollProgressProps>(({ className, gradient = true, color, hideWhenNotScrollable = true, springConfig, ...props }, ref) => {
    const { scrollYProgress } = useScroll()
    const prefersReducedMotion = useReducedMotion()

    // Smooth spring animation for the progress bar
    const scaleX = useSpring(scrollYProgress, {
        stiffness: springConfig?.stiffness ?? 100,
        damping: springConfig?.damping ?? 30,
        restDelta: 0.001
    })

    // Track if the page has enough content to scroll
    const [isScrollable, setIsScrollable] = React.useState(true)

    React.useEffect(() => {
        const checkScrollable = () => {
            // Add 100px buffer to avoid showing progress on barely-scrollable content
            const scrollable = document.documentElement.scrollHeight > window.innerHeight + 100
            setIsScrollable(scrollable)
        }

        // Check on mount
        checkScrollable()

        // Re-check on resize (content may reflow)
        window.addEventListener("resize", checkScrollable)

        // Also check when images/content loads
        window.addEventListener("load", checkScrollable)

        return () => {
            window.removeEventListener("resize", checkScrollable)
            window.removeEventListener("load", checkScrollable)
        }
    }, [])

    // Don't render if content doesn't need scrolling
    if (!isScrollable && hideWhenNotScrollable) {
        return null
    }

    return (
        <motion.div
            ref={ref}
            className={cn(
                // Positioning: fixed at top, full width, above navigation
                "fixed left-0 right-0 top-0 z-[2100]",
                // Transform origin for scale animation
                "origin-left",
                // Height: 8px on mobile, 6px on desktop (prominent, premium feel)
                "h-[8px] md:h-[6px]",
                // Gradient from brand pink to deeper fuchsia (rewards progress visually)
                gradient && "bg-gradient-to-r from-brand-pink-400 to-fuchsia-500",
                className
            )}
            style={{
                // Use spring animation, or instant for reduced motion preference
                scaleX: prefersReducedMotion ? scrollYProgress : scaleX,
                // Allow custom solid color when gradient is disabled
                backgroundColor: !gradient && color ? color : undefined
            }}
            // Accessibility
            role="progressbar"
            aria-label="Reading progress"
            aria-valuemin={0}
            aria-valuemax={100}
            {...props}
        />
    )
})

ScrollProgress.displayName = "ScrollProgress"

export { ScrollProgress }
